import { useMemo, useState } from "react";
import { Download, GitCompare, Loader2, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { DeleteDialog } from "@/components/delete-dialog";
import { api } from "@/lib/api";
import { fetchVideoDownloadUrl, triggerDownload } from "@/hooks/use-videos";
import type { Folder, Video } from "@/lib/api-types";

/**
 * SelectionActionBar (2026-07-18) — bottom-anchored bar that slides in when
 * one or more assets/folders are selected on project-detail or folder-detail.
 *
 * Frame.io parity:
 *   Left: "N Asset(s) selected • X.XX MB" + × close button.
 *   Right: Compare (only when EXACTLY 2 videos selected — Phase B placeholder)
 *          / Download / Delete.
 *
 * Byte size uses the video rows' fileSizeBytes only. Selected folders don't
 * contribute a size to the header — enumerating a folder's contents would be
 * an extra round-trip we only pay when the user actually clicks Download.
 * Frame.io behaves the same.
 */

export type SelectionActionBarProps = {
  /** Selected video row references. Must include id + title + cdnUrl + fileSizeBytes. */
  selectedVideos: Video[];
  /** Selected folder ids. Names optional — used only for the delete confirmation title. */
  selectedFolders: Folder[];
  /** Fires after Delete Permanently or Move to Trash succeeds. Callers clear their selection sets here. */
  onCleared: () => void;
  /** Explicit close button (× on the left) fires this. Callers just clear their selection sets. */
  onDismiss: () => void;
  /** Trash the video(s). Called once with the id list — caller fans out mutations in parallel. */
  onTrashVideos: (videoIds: string[]) => Promise<void>;
  /** Permanent-delete the video(s). Same shape. */
  onPermanentDeleteVideos: (videoIds: string[]) => Promise<void>;
  /**
   * Phase B compare handler. Called only when exactly 2 videos selected + both
   * assetType === "video". Wired to a route push in the caller. Leave undefined
   * to render the placeholder (disabled with "Coming soon" tooltip).
   */
  onCompare?: (videoAId: string, videoBId: string) => void;
};

export function SelectionActionBar({
  selectedVideos,
  selectedFolders,
  onCleared,
  onDismiss,
  onTrashVideos,
  onPermanentDeleteVideos,
  onCompare,
}: SelectionActionBarProps) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const totalCount = selectedVideos.length + selectedFolders.length;
  const totalBytes = useMemo(
    () => selectedVideos.reduce((sum, v) => sum + (v.fileSizeBytes ?? 0), 0),
    [selectedVideos],
  );

  // Compare precondition: exactly 2 selected AND both are videos (not audio /
  // image), AND no folders in the mix. Anything else greys the button out.
  const canCompare =
    !!onCompare &&
    selectedFolders.length === 0 &&
    selectedVideos.length === 2 &&
    selectedVideos.every((v) => (v.assetType ?? "video") === "video");

  async function handleDownload() {
    if (downloading) return;
    setDownloading(true);
    try {
      // Collect video ids from the direct selection first.
      const videoIds: string[] = selectedVideos.map((v) => v.id);

      // Recursively enumerate every selected folder's videos. GET /folders/:id/contents
      // returns { subfolders, videos, folder }. Breadth-first walk via a queue.
      const seenFolderIds = new Set<string>();
      const queue = [...selectedFolders.map((f) => f.id)];
      while (queue.length > 0) {
        const folderId = queue.shift()!;
        if (seenFolderIds.has(folderId)) continue;
        seenFolderIds.add(folderId);
        try {
          const contents = await api.get<{
            subfolders: { id: string }[];
            videos: Video[];
          }>(`/editor/folders/${folderId}/contents`);
          for (const v of contents.videos) videoIds.push(v.id);
          for (const s of contents.subfolders) queue.push(s.id);
        } catch {
          // Skip inaccessible folders — restricted folder ACL, deleted, etc.
        }
      }

      if (videoIds.length === 0) return;

      // Fetch a presigned download URL per video (URLs carry Content-Disposition:
      // attachment via the signed response-content-disposition query — see
      // worker/routes/editor.js "GET /videos/:id/download"). Skip cdnUrl for
      // downloads: it's a public CDN URL without disposition, so browsers open
      // it in a new tab instead of saving.
      for (let i = 0; i < videoIds.length; i++) {
        try {
          const { downloadUrl, filename } = await fetchVideoDownloadUrl(videoIds[i]);
          triggerDownload(downloadUrl, filename);
        } catch {
          // Non-fatal — one bad video shouldn't block the rest. Could surface
          // a toast if we had a toast system wired up.
        }
        // 250ms stagger so browsers don't drop rapid-fire downloads as one
        // "multiple downloads" click. Chrome's downloadURL rate limiter kicks
        // in at ~10/s; slower stagger keeps us well under.
        if (i < videoIds.length - 1) {
          await new Promise((r) => setTimeout(r, 250));
        }
      }
    } finally {
      setDownloading(false);
    }
  }

  const videoIds = useMemo(() => selectedVideos.map((v) => v.id), [selectedVideos]);
  // Bulk delete of videos only. Folder delete is a separate concern (endpoint
  // is different, cascade semantics differ). Phase A intentionally skips
  // folder-in-bulk-delete — user gets a UX hint by seeing folders excluded
  // from the count in the confirmation. Add if user asks.
  const deleteCount = videoIds.length;
  const deleteLabel = deleteCount === 1 ? selectedVideos[0].title : "";

  const canDelete = deleteCount > 0;

  return (
    <>
      {/* The bar. Fixed at the bottom of the viewport for both project + folder
          detail pages. Doesn't overlap the rail since the pages are already
          inside a right-of-rail layout on desktop. */}
      <div
        data-selection-bar
        className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-4"
      >
        <div className="pointer-events-auto flex w-full max-w-4xl items-center gap-3 rounded-xl border border-border bg-popover px-3 py-2 shadow-2xl">
          {/* Left: × close + count/size label */}
          <button
            type="button"
            aria-label="Clear selection"
            onClick={onDismiss}
            className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            <X className="h-4 w-4" strokeWidth={1.75} />
          </button>

          <div className="min-w-0 flex-1 truncate text-sm">
            <span className="font-medium text-foreground">
              {totalCount} {totalCount === 1 ? "Asset" : "Assets"} selected
            </span>
            {/* Size chunk hidden on mobile — no room next to icon buttons.
                Desktop keeps the "• X.XX MB" summary. */}
            {totalBytes > 0 && (
              <span className="hidden md:inline">
                <span className="mx-2 text-muted-foreground">•</span>
                <span className="text-muted-foreground tabular-nums">
                  {formatBytes(totalBytes)}
                </span>
              </span>
            )}
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-1.5">
            {/* Compare — only when the precondition is met. Placeholder tooltip
                explains why it's disabled when the caller passes onCompare but
                the selection doesn't meet the rule. */}
            {/* Mobile: icon-only (labels hidden). Tooltips still expose the
                names on hover / long-press. Desktop keeps full text. */}
            {onCompare && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="outline"
                      size="sm"
                      aria-label="Compare assets"
                      disabled={!canCompare}
                      onClick={() => {
                        if (!canCompare) return;
                        onCompare(selectedVideos[0].id, selectedVideos[1].id);
                      }}
                    >
                      <GitCompare className="h-4 w-4" />
                      <span className="hidden md:inline">Compare Assets</span>
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {canCompare ? "Compare Assets" : "Select exactly two videos to compare"}
                </TooltipContent>
              </Tooltip>
            )}
            {!onCompare && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button variant="outline" size="sm" aria-label="Compare assets" disabled>
                      <GitCompare className="h-4 w-4" />
                      <span className="hidden md:inline">Compare Assets</span>
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">Coming soon</TooltipContent>
              </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="Download"
                  onClick={handleDownload}
                  disabled={downloading || (selectedVideos.length === 0 && selectedFolders.length === 0)}
                >
                  {downloading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  <span className="hidden md:inline">
                    {downloading ? "Downloading…" : "Download"}
                  </span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {downloading ? "Downloading…" : "Download"}
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="Delete"
                  onClick={() => setDeleteOpen(true)}
                  disabled={!canDelete}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="hidden md:inline">Delete</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">Delete</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>

      <DeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        itemLabel="asset"
        itemName={deleteLabel}
        itemCount={deleteCount}
        onTrash={async () => {
          await onTrashVideos(videoIds);
          onCleared();
        }}
        onPermanent={async () => {
          await onPermanentDeleteVideos(videoIds);
          onCleared();
        }}
      />
    </>
  );
}

// Small binary-unit formatter — same 1024 base as the rest of the app.
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
